import { MdEmail } from 'react-icons/md';
import { Seo } from '../../core/seo';

const DMCA = () => {
  return (
    <div className=" text-[#284F60] min-h-screen py-12 px-6">
      <Seo
        title="DMCA / Copyright"
        description="HirePanther DMCA and copyright policy. How to report alleged copyright infringement and how we handle reports."
        canonicalPath="/dmca"
      />
      <div className="max-w-4xl mx-auto px-6  py-12 text-[#284F60]">
        {/* Page Title */}
        <h1 className="text-4xl font-extrabold text-left text-[#284F60] mb-6">
          DMCA / Copyright Notices
        </h1>

        {/* Dates */}
        <p className="text-sm text-gray-500 text-left mb-12">
          <span className="block">
            Effective Date: December 01, 2025 | Last Updated: December 01, 2025
          </span>
        </p>

        {/* Intro */}
        <p className="text-lg leading-relaxed mb-6">
          HirePanther (“we,” “our,” “us”) respects the intellectual property
          rights of others and expects our users to do the same. This policy
          describes how to report alleged copyright infringement and how we
          handle such reports.
        </p>

        {/* Section 1 */}
        <h2 className="text-2xl font-semibold text-[#2D617A] mt-8 mb-3">
          1. Policy Overview
        </h2>
        <ul className="list-disc pl-6 space-y-2 text-[#284F60 ml-4]">
          <li>
            Users may not upload, post, or share content on HirePanther that
            infringes the intellectual property rights of others.
          </li>
          <li>
            We will respond to valid takedown notices in accordance with the
            U.S. Digital Millennium Copyright Act (DMCA) and Canada’s
            Notice-and-Notice regime.
          </li>
          <li>
            Repeated infringement may result in suspension or termination of
            user accounts.
          </li>
        </ul>

        {/* Section 2 */}
        <h2 className="text-2xl font-semibold text-[#2D617A] mt-8 mb-3">
          2. Filing a DMCA Takedown Notice
        </h2>
        <p className="mb-4">
          If you believe your copyrighted work has been copied or used in a way
          that constitutes infringement, please submit a written notice with the
          following information:
        </p>
        <ul className="list-disc pl-6 space-y-2 text-[#284F60 ml-4]">
          <li>Your contact information (full name, address, phone, email).</li>
          <li>Description of the copyrighted work you claim is infringed.</li>
          <li>Location/URL of the infringing material on HirePanther.</li>
          <li>
            Statement of good faith belief that the disputed use is not
            authorized by the copyright owner, its agent, or the law.
          </li>
          <li>
            Statement under penalty of perjury that the information in the
            notice is accurate and that you are the copyright owner or
            authorized to act on the owner’s behalf.
          </li>
          <li>
            Electronic or physical signature of the copyright owner or
            authorized agent.
          </li>
        </ul>

        {/* Contact for DMCA */}
        <div className="mt-6 bg-gray-50 border rounded-lg p-5 shadow-sm">
          <h3 className="text-lg font-medium text-[#284F60] mb-2">
            Send notices to:
          </h3>
          <p className="flex items-center gap-2 text-[#284F60]">
            <MdEmail className="text-[#284F60] text-xl" />
            <a
              href="mailto:dmca@hirepanther.ca"
              className="text-blue-500 hover:underline"
            >
              dmca@hirepanther.ca
            </a>
          </p>
        </div>

        {/* Section 3 */}
        <h2 className="text-2xl font-semibold text-[#2D617A] mt-8 mb-3">
          3. Counter-Notice (for Providers/Users)
        </h2>
        <p className="mb-4">
          If you believe your content was removed in error, you may submit a
          counter-notice containing:
        </p>
        <ul className="list-disc pl-6 space-y-2 text-[#284F60 ml-4]">
          <li>Your name, address, phone, and email.</li>
          <li>
            Identification of the removed material and its prior location on
            HirePanther.
          </li>
          <li>
            A statement under penalty of perjury that you believe the removal
            was a mistake or misidentification.
          </li>
          <li>
            Consent to jurisdiction of the courts in Ontario, Canada (or, if
            outside Canada, to the jurisdiction where HirePanther is located).
          </li>
          <li>Your physical or electronic signature.</li>
        </ul>
        <p className="mt-4">
          Upon receipt of a valid counter-notice, we may restore the material
          unless the original complainant files court action within 10 business
          days.
        </p>

        {/* Section 4 */}
        <h2 className="text-2xl font-semibold text-[#2D617A] mt-8 mb-3">
          4. Repeat Infringers
        </h2>
        <p className="text-[#284F60] leading-relaxed">
          We may terminate accounts of users who are subject to repeated
          infringement notices.
        </p>

        {/* Section 5 */}
        <h2 className="text-2xl font-semibold text-[#2D617A] mt-8 mb-3">
          5. Other Notices
        </h2>
        <ul className="list-disc pl-6 space-y-2 text-[#284F60] ml-4">
          <li>
            Trademark violations (use of logos, business names without
            authorization).
          </li>
          <li>Other legal complaints (defamation, privacy violations).</li>
        </ul>
        <p className="mt-2 text-[#284F60] leading-relaxed">
          Each report will be reviewed on a case-by-case basis.
        </p>

        {/* Section 6 */}
        <h2 className="text-2xl font-semibold text-[#2D617A] mt-8 mb-3">
          6. Important Disclaimers
        </h2>
        <ul className="list-disc pl-6 space-y-2 text-[#284F60]">
          <li>
            HirePanther does not mediate disputes between users beyond the
            notice process.
          </li>
          <li>
            Submitting false or misleading notices may result in liability for
            damages (legal and financial).
          </li>
          <li>
            We recommend seeking legal advice before filing a notice or
            counter-notice.
          </li>
        </ul>

        {/* Contact Again */}
        <h2 className="text-2xl font-semibold text-[#2D617A] mt-8 mb-3">
          7. Contact Us
        </h2>
        <p className="mb-4">For DMCA/Notice matters, contact:</p>
        <div className="bg-gray-50 border rounded-lg p-5 shadow-sm">
          <p className="flex items-center gap-2 text-[#284F60]">
            <MdEmail className="text-[#284F60] text-xl" />
             <a
              href="mailto:dmca@hirepanther.ca"
              className="text-blue-500 hover:underline"
            >
              dmca@hirepanther.ca
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default DMCA;
